import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../utils/apiConfig";
import AddAthleteProfileModal from "./AddAthleteProfileModal";
import DeleteAthleteModal from "./DeleteAthleteModal";
import AthleticOutlookActions from "./AthleticOutlookActions";
import AthleticOutlookModal, { AthleticOutlookAthlete } from "./AthleticOutlookModal";
import { useAuth } from "../contexts/AuthContext";
import { canManageAthletes, isCoach } from "../utils/auth";
import { formatLastLoginDate } from "../utils/dateDisplay";
import { parseApiErrorMessage } from "../utils/apiErrorMessage";
import "../styles/athletes-table.css";
import { apiClient } from "../utils/apiClient";


type AthleteRow = {
    id?: string;
    athleteId?: string | number;  // Backend might return athleteId instead of id
    /** Auth user id — required for /api/athletes/add/view when editing (distinct from athlete profile id) */
    userId?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    parentEmail?: string;
    parentEmail2?: string;
    guardianEmail?: string;
    location?: string;
    imageUrl?: string;
    profilePictureUrl?: string;
    title?: string;
    state?: string;
    stateId?: number;
    staffName?: string;
    staffId?: number;
    lastLoginTime?: string;
    sportName?: string;
    hasAthleticOutlook?: boolean;
    canEditAthleticOutlook?: boolean;
    canCreateAthleticOutlook?: boolean;
};
type AthleteList = {
    rows?: AthleteRow[];
    athletes?: AthleteRow[];
    totalAthletes?: number;
    totalElements?: number;
    totalPages?: number;
    content?: AthleteRow[];
};

type Props = {
    userId: string;
    refreshTrigger?: number;
    onAddAthleteClick?: () => void;
    /** Clever staff/admin: show empty-state CTA to provision students */
    showProvisionStudent?: boolean;
    onProvisionStudentClick?: () => void;
    /** Staff/admin (company or Clever): bulk upload */
    showBulkUpload?: boolean;
    onBulkUploadClick?: () => void;
};

export default function AthletesTable({
    userId,
    refreshTrigger,
    onAddAthleteClick,
    showProvisionStudent,
    onProvisionStudentClick,
    showBulkUpload,
    onBulkUploadClick,
}: Props) {
    const [rows, setRows] = useState<AthleteRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedAthlete, setSelectedAthlete] = useState<AthleteRow | null>(null);
    const [athleteToDelete, setAthleteToDelete] = useState<AthleteRow | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [outlookAthlete, setOutlookAthlete] = useState<AthleticOutlookAthlete | null>(null);
    const [outlookStartInEdit, setOutlookStartInEdit] = useState(false);
    const [teamMemberInfo, setTeamMemberInfo] = useState<{ firstName?: string; lastName?: string; displayName?: string } | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState<number | undefined>(undefined);
    const navigate = useNavigate();
    const { authProvider } = useAuth();
    const isCleverUser = authProvider === "clever";
    const canManageAthletesOnPage = canManageAthletes();
    const hasLoadedRef = useRef(false);

    // Helper function to get image URL from athlete (supports both imageUrl and profilePictureUrl)
    const getAthleteImageUrl = (athlete: AthleteRow): string | undefined => {
        return athlete.profilePictureUrl || athlete.imageUrl;
    };

    // Helper function to get initials from athlete name
    const getAthleteInitials = (athlete: AthleteRow): string => {
        const first = athlete.firstName?.charAt(0) || '';
        const last = athlete.lastName?.charAt(0) || '';
        return `${first}${last}`.toUpperCase();
    };

    // Fetch team member profile information
    useEffect(() => {
        const fetchTeamMemberInfo = async () => {
            if (!userId) return;

            try {
                const response = await apiClient(buildApiUrl(`api/admins/${encodeURIComponent(userId)}/profile`), {
                    headers: { Accept: "application/json" },
                    credentials: "include" // Use HTTP-only cookie for auth
                });

                if (response.ok) {
                    const data = await response.json();
                    setTeamMemberInfo({
                        firstName: data.firstName,
                        lastName: data.lastName,
                        displayName: data.displayName
                    });
                }
            } catch (error) {
                // Failed to fetch team member info
            }
        };

        fetchTeamMemberInfo();
    }, [userId]);

    const fetchAthletes = useCallback(async (opts?: { silent?: boolean }) => {
        if (!userId) {
            setRows([]);
            setLoading(false);
            return;
        }

        const silent = opts?.silent ?? false;
        if (!silent) {
            setLoading(true);
        }
        setError(null);

        try {
            // Build URL with pagination parameters
            const params = new URLSearchParams({
                page: currentPage.toString(),
                size: pageSize.toString(),
                sort: "id"
            });

            const url = userId
                ? buildApiUrl(`api/admins/${userId}/athletes?${params}`)
                : buildApiUrl(`api/athletes?${params}`);

            const response = await apiClient(url, {
                headers: { Accept: "application/json" },
                credentials: "include"
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data: AthleteList = await response.json();

            // Handle paginated response format (Spring Boot style)
            if (data.content && Array.isArray(data.content)) {
                setRows(data.content);
                setTotal(data.totalElements ?? data.content.length);
                setTotalPages(data.totalPages ?? 1);
            } else {
                // Handle both API response formats: {rows: [...]} and {athletes: [...]}
                const athleteArray = data?.athletes || data?.rows || [];
                const athleteList = Array.isArray(athleteArray) ? athleteArray : [];
                setRows(athleteList);
                setTotal(data?.totalAthletes || data?.totalElements || athleteList.length);
                setTotalPages(data?.totalPages || (athleteList.length > 0 ? 1 : 0));
            }
        } catch (error) {
            setError("Failed to load athletes");
            setRows([]);
            setTotal(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [userId, currentPage, pageSize]);

    useEffect(() => {
        fetchAthletes({ silent: hasLoadedRef.current });
        hasLoadedRef.current = true;
    }, [fetchAthletes, refreshTrigger]);

    const handleRowClick = (athlete: AthleteRow, index: number, event: React.MouseEvent) => {
        // Don't navigate if edit button was clicked
        if ((event.target as HTMLElement).closest('.edit-btn')) {
            return;
        }

        // Prepare team member info for breadcrumb
        const teamMemberName = teamMemberInfo?.displayName ||
            `${teamMemberInfo?.firstName || ''} ${teamMemberInfo?.lastName || ''}`.trim();

        // Use id or athleteId field (backend might return either)
        const id = athlete.id || athlete.athleteId?.toString();
        if (id) {
            const athleteDisplayName =
                `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim() || undefined;
            navigate(`/athlete/profile/${id}`, {
                state: {
                    breadcrumbOrigin: 'team',
                    teamMemberId: userId,
                    teamMemberName: teamMemberName || 'Staff',
                    ...(athleteDisplayName ? { athleteDisplayName } : {}),
                },
            });
        }
    };

    const toOutlookAthlete = (athlete: AthleteRow): AthleticOutlookAthlete | null => {
        const athleteId = Number(athlete.athleteId ?? athlete.id);
        if (!Number.isFinite(athleteId) || athleteId <= 0) {
            return null;
        }
        return {
            athleteId,
            firstName: athlete.firstName,
            lastName: athlete.lastName,
            sportName: athlete.sportName,
            imageUrl: getAthleteImageUrl(athlete),
        };
    };

    const handleOutlookClick = (athlete: AthleteRow, event: React.MouseEvent, startInEdit: boolean) => {
        event.stopPropagation();
        const target = toOutlookAthlete(athlete);
        if (!target) return;
        setOutlookStartInEdit(startInEdit);
        setOutlookAthlete(target);
    };

    const handleEditClick = (athlete: AthleteRow, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent row click navigation

        setSelectedAthlete(athlete);
        setShowEditModal(true);
    };

    const handleDeleteClick = (athlete: AthleteRow, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent row click navigation

        setDeleteError(null);
        setAthleteToDelete(athlete);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!athleteToDelete) return;

        setDeleteLoading(true);
        setDeleteError(null);

        try {
            const athleteId = athleteToDelete.id || athleteToDelete.athleteId?.toString();

            if (!athleteId) {
                throw new Error("Athlete ID not found");
            }

            const response = await apiClient(buildApiUrl(`api/athletes/${athleteId}`), {
                method: "DELETE",
                headers: { Accept: "application/json" },
                credentials: "include"
            });

            if (!response.ok) {
                const message = await parseApiErrorMessage(
                    response,
                    "Failed to delete athlete. Please try again.",
                );
                setDeleteError(message);
                return;
            }

            await fetchAthletes({ silent: true });

            setShowDeleteModal(false);
            setAthleteToDelete(null);
            setDeleteError(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to delete athlete. Please try again.";
            setDeleteError(message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setAthleteToDelete(null);
        setDeleteError(null);
        setDeleteLoading(false);
    };

    const handleEditSuccess = () => {
        fetchAthletes({ silent: true });
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setSelectedAthlete(null);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const canGoPrevious = currentPage > 0;
    const canGoNext = currentPage < totalPages - 1;
    const showPagination = !loading && !error && rows.length > 0 && totalPages > 1;

    // Generate page numbers with ellipsis
    const getPageNumbers = () => {
        const pages: (number | 'ellipsis')[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible + 2) {
            // Show all pages if total is small
            for (let i = 0; i < totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(0);

            if (currentPage <= 2) {
                // Near start: 1, 2, 3, 4, ..., last
                for (let i = 1; i <= 3; i++) {
                    pages.push(i);
                }
                pages.push('ellipsis');
                pages.push(totalPages - 1);
            } else if (currentPage >= totalPages - 3) {
                // Near end: 1, ..., last-3, last-2, last-1, last
                pages.push('ellipsis');
                for (let i = totalPages - 4; i < totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // Middle: 1, ..., current-1, current, current+1, ..., last
                pages.push('ellipsis');
                pages.push(currentPage - 1);
                pages.push(currentPage);
                pages.push(currentPage + 1);
                pages.push('ellipsis');
                pages.push(totalPages - 1);
            }
        }

        return pages;
    };

    if (loading && rows.length === 0) return <div className="ath-card">Loading athletes…</div>;
    if (error)   return <div className="ath-card ath-error">{error}</div>;

    if (rows.length === 0) {
        // Coaches only see athletes in their assigned sports (API already filters).
        // Hide the empty state entirely when there is no overlap.
        if (isCoach() && !canManageAthletesOnPage) {
            return null;
        }

        const handleAddAthleteClick = () => {
            if (onAddAthleteClick) {
                onAddAthleteClick();
            }
        };

        const handleProvisionStudentClick = () => {
            if (onProvisionStudentClick) {
                onProvisionStudentClick();
            }
        };

        const handleBulkUploadClick = () => {
            if (onBulkUploadClick) {
                onBulkUploadClick();
            }
        };

        return (
            <>
                <div className="ath-card">
                    <div className="ath-empty-wrap">
                        <div className="ath-illustration" aria-hidden="true">
                            <div className="ath-plus">+</div>
                        </div>
                        <div className="ath-empty-title">No Athletes Yet</div>
                        <p className="ath-empty-text">
                            Once you add your first athlete, you'll be able to collaborate, and start
                            making progress together.
                        </p>
                        <div className="ath-empty-actions">
                            {!isCleverUser && canManageAthletesOnPage && (
                                <button onClick={handleAddAthleteClick} className="btn ath-empty-cta" type="button">
                                    <span className="btn-icon-circle">+</span>
                                    <span className="btn-text">Add athlete</span>
                                </button>
                            )}
                            {showProvisionStudent && (
                                <button
                                    onClick={handleProvisionStudentClick}
                                    className="btn ath-empty-cta"
                                    type="button"
                                >
                                    <span className="btn-icon-circle">+</span>
                                    <span className="btn-text">Provision student</span>
                                </button>
                            )}
                            {showBulkUpload && onBulkUploadClick && (
                                <button
                                    onClick={handleBulkUploadClick}
                                    className="btn ath-empty-cta"
                                    type="button"
                                >
                                    <span className="btn-icon-circle">+</span>
                                    <span className="btn-text">Bulk upload</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="ath-card">
                <div className="ath-table ath-table--with-preread">
                    <div className="ath-row ath-head">
                        <div className="ath-cell ath-col-avatar" />
                        <div className="ath-cell">First</div>
                        <div className="ath-cell">Last</div>
                        <div className="ath-cell">Email address</div>
                        <div className="ath-cell">Last login</div>
                        <div className="ath-cell">Actions</div>
                    </div>

                    {rows.map((r, i) => (
                        <div
                            className="ath-row ath-row-clickable"
                            key={i}
                            onClick={(event) => {
                                // Don't navigate if edit or delete button was clicked
                                if ((event.target as HTMLElement).closest('.ath-edit-btn') ||
                                    (event.target as HTMLElement).closest('.ath-delete-btn') ||
                                    (event.target as HTMLElement).closest('.ath-preread-btn') ||
                                    (event.target as HTMLElement).closest('.ath-add-preread-btn')) {
                                    return;
                                }
                                handleRowClick(r, i, event);
                            }}
                            style={{ cursor: 'pointer' }}
                            title={(r.id || r.athleteId) ? `View ${r.firstName} ${r.lastName}'s profile` : `View ${r.firstName || 'Athlete'} ${r.lastName || ''}'s profile`}
                        >
                            <div className="ath-cell ath-col-avatar">
                                <div className="ath-avatar">
                                    {getAthleteImageUrl(r) ? (
                                        <img src={getAthleteImageUrl(r)} alt="" />
                                    ) : (
                                        <span className="ath-avatar-initials">{getAthleteInitials(r)}</span>
                                    )}
                                </div>
                            </div>
                            <div className="ath-cell">{r.firstName || "—"}</div>
                            <div className="ath-cell">{r.lastName || "—"}</div>
                            <div className="ath-cell">{r.email || "—"}</div>
                            <div className="ath-cell">{formatLastLoginDate(r.lastLoginTime)}</div>
                            <div className="ath-cell ath-actions-cell">
                                {canManageAthletesOnPage && (
                                    <>
                                        <button
                                            className="ath-edit-btn"
                                            onClick={(event) => handleEditClick(r, event)}
                                            data-athlete-id={r.id || r.athleteId || ''}
                                            title={`Edit ${r.firstName} ${r.lastName}`}
                                            aria-label="Edit"
                                        >
                                            ✎
                                        </button>
                                        <button
                                            className="ath-delete-btn"
                                            onClick={(event) => handleDeleteClick(r, event)}
                                            data-athlete-id={r.id || r.athleteId || ''}
                                            title={`Delete ${r.firstName} ${r.lastName}`}
                                            aria-label="Delete"
                                        >
                                            🗑
                                        </button>
                                    </>
                                )}
                                <AthleticOutlookActions
                                    hasAthleticOutlook={r.hasAthleticOutlook}
                                    canCreateAthleticOutlook={r.canCreateAthleticOutlook}
                                    athleteName={`${r.firstName || ""} ${r.lastName || ""}`.trim()}
                                    onView={(event) => handleOutlookClick(r, event, false)}
                                    onCreate={(event) => handleOutlookClick(r, event, true)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showPagination && (
                <div className="ath-pagination" data-testid="athlete-pagination">
                    <div className="ath-pagination-info">
                        <span>
                            Showing <strong>{currentPage * pageSize + 1}-{Math.min(currentPage * pageSize + rows.length, total || 0)}</strong> of <strong>{total || 0}</strong> students
                        </span>
                    </div>

                    <div className="ath-pagination-controls">
                        <div className="ath-page-nav">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={!canGoPrevious}
                                className="ath-page-btn"
                                aria-label="Previous page"
                                data-testid="pagination-previous"
                            >
                                Previous
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === 'ellipsis') {
                                    return (
                                        <span key={`ellipsis-${index}`} className="ath-page-ellipsis">
                                            ...
                                        </span>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`ath-page-number ${page === currentPage ? 'ath-page-number-active' : ''}`}
                                        aria-label={`Page ${page + 1}`}
                                        aria-current={page === currentPage ? 'page' : undefined}
                                        data-testid={`page-number-${page + 1}`}
                                    >
                                        {page + 1}
                                    </button>
                                );
                            })}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={!canGoNext}
                                className="ath-page-btn"
                                aria-label="Next page"
                                data-testid="pagination-next"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AddAthleteProfileModal
                open={showEditModal}
                onClose={handleCloseModal}
                onSaved={handleEditSuccess}
                initial={selectedAthlete || undefined}
            />

            <DeleteAthleteModal
                open={showDeleteModal}
                athleteName={`${athleteToDelete?.firstName || ''} ${athleteToDelete?.lastName || ''}`.trim()}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                loading={deleteLoading}
                error={deleteError ?? undefined}
            />

            <AthleticOutlookModal
                open={!!outlookAthlete}
                athlete={outlookAthlete}
                startInEdit={outlookStartInEdit}
                onClose={() => setOutlookAthlete(null)}
                onSaved={() => fetchAthletes({ silent: true })}
            />
        </>
    );
}